import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Pause, SkipForward, Clock, Zap, Loader2, Bookmark, Radio, Cast } from 'lucide-react';
import Layout from '../components/Layout';
import AiShoutoutInbox from '../components/AiShoutoutInbox';
import { useAuth } from '../contexts/AuthContext';
import { useAiPlayer } from '../contexts/AiPlayerContext';
import { CROSSFADE_MS } from '../utils/aiMusicPlayer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER = '/media-art/mbai-cover.jpg';

const timeOfDay = (hour) => {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'late night';
};

// Scoped neon/retro-arcade keyframes — kept local so the rest of the app is untouched.
const ArcadeStyles = () => (
  <style>{`
    @keyframes mb-eq { 0%,100%{transform:scaleY(0.35)} 50%{transform:scaleY(1)} }
    @keyframes mb-marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
    @keyframes mb-breathe { 0%,100%{box-shadow:0 0 40px -6px rgba(255,0,127,0.55)} 50%{box-shadow:0 0 70px -2px rgba(0,255,255,0.55)} }
    .mb-scanlines{background-image:linear-gradient(rgba(0,0,0,0) 50%, rgba(0,0,0,0.22) 50%);background-size:100% 4px;}
    .mb-eqbar{transform-origin:bottom;animation:mb-eq 0.9s ease-in-out infinite;}
  `}</style>
);

export default function AiControlBoothPage() {
  const { user } = useAuth();
  const {
    current, nextTrack, progress, playing, settings, taxonomy,
    play, pause, skip, setMode, applyMood, applyPreset, loadAll,
  } = useAiPlayer();

  const [draftEnergy, setDraftEnergy] = useState(null);
  const [draftGenre, setDraftGenre] = useState(null);
  const [applying, setApplying] = useState(false);
  const [presets, setPresets] = useState([]);
  const [applyingPreset, setApplyingPreset] = useState(null);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/settings/ai-mode/presets`)
      .then((r) => { if (alive) setPresets(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const onApplyPreset = async (p) => {
    setApplyingPreset(p.id);
    try {
      await applyPreset(p.id);
      toast.success(`Now playing "${p.name}" — ${p.genre.replace(/-/g, ' ')} · ${p.energy}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not switch vibe');
    } finally {
      setApplyingPreset(null);
    }
  };

  useEffect(() => {
    if (!settings) return;
    if (settings.mode === 'manual') {
      setDraftEnergy(settings.manual_energy || null);
      setDraftGenre((settings.manual_genres || [])[0] || settings.baseline_genre || null);
    } else {
      setDraftEnergy(settings.resolved_energy_now || null);
      setDraftGenre(settings.baseline_genre || null);
    }
  }, [settings]);

  const applyMoodClick = async () => {
    if (!draftEnergy || !draftGenre) return toast.error('Pick an energy and a genre first');
    setApplying(true);
    try {
      await applyMood(draftEnergy, draftGenre);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const remainingS = Math.max(0, (progress.duration || 0) - (progress.currentTime || 0));
  const crossfadeSoon = progress.remainingMs > 0 && progress.remainingMs <= CROSSFADE_MS;
  const pct = progress.duration > 0 ? Math.min(100, ((progress.currentTime || 0) / progress.duration) * 100) : 0;

  const displayGenre = useMemo(() => {
    if (current?.genres?.length) return current.genres[0];
    if (settings?.mode === 'manual') return (settings.manual_genres || [])[0] || settings.baseline_genre;
    return settings?.baseline_genre;
  }, [current, settings]);

  const displayEnergy = current?.energy || settings?.resolved_energy_now || '—';
  const hour = settings?.current_hour ?? new Date().getHours();
  const tod = timeOfDay(hour);
  const status = playing ? 'PLAYING' : (current ? 'PAUSED' : 'READY');
  const statusColor = playing ? '#39FF14' : '#FF007F';

  return (
    <Layout>
      <ArcadeStyles />
      {/* Neon arcade backdrop */}
      <div className="relative min-h-full" data-testid="ai-booth-hero">
        <div
          className="fixed inset-0 pointer-events-none opacity-60"
          style={{ backgroundImage: `url(${'/media-art/mbai-bg.jpg'})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="fixed inset-0 pointer-events-none bg-[#0A0A0E]/80" />
        <div className="fixed inset-0 pointer-events-none mb-scanlines opacity-40" />

        <div className="relative p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
          {/* LED marquee header */}
          <div className="rounded-2xl overflow-hidden border-2 border-[#FF007F]/50 bg-black/40 shadow-[0_0_20px_-6px_#FF007F]">
            <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap py-2">
              <div className="flex shrink-0" style={{ animation: 'mb-marquee 18s linear infinite' }}>
                {[0, 1].map((k) => (
                  <span key={k} className="px-6 text-sm font-extrabold uppercase tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#FFD700] to-[#00FFFF]">
                    ★ MusicBooth AI • Live Venue Broadcast ★ MusicBooth AI • Live Venue Broadcast
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Sub-header + mode toggle */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-[11px] uppercase tracking-[0.35em] text-gray-400 font-bold flex items-center gap-2">
              <Cast className="w-3.5 h-3.5 text-[#00FFFF]" /> {(user?.name || 'venue').toLowerCase()} · player
            </p>
            {settings && (
              <div className="bg-black/50 rounded-full p-1 flex ring-1 ring-white/10" data-testid="ai-booth-mode-toggle">
                <button type="button" onClick={() => settings.mode !== 'auto' && setMode('auto')} data-testid="ai-mode-auto"
                  className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all flex items-center gap-1.5 ${settings.mode === 'auto' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}>
                  <Clock className="w-3 h-3" /> Auto
                </button>
                <button type="button" onClick={() => settings.mode !== 'manual' && setMode('manual')} data-testid="ai-mode-manual"
                  className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all flex items-center gap-1.5 ${settings.mode === 'manual' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}>
                  <Zap className="w-3 h-3" /> Manual
                </button>
              </div>
            )}
          </div>

          {/* Quick vibes */}
          {presets.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="ai-booth-quick-vibes">
              <span className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mr-1 flex items-center gap-1.5">
                <Bookmark className="w-3.5 h-3.5 text-[#FF007F]" /> Quick vibes
              </span>
              {presets.map((p) => {
                const activeVibe = settings && settings.mode === p.mode && settings.baseline_genre === p.genre && settings.baseline_energy === p.energy;
                return (
                  <button key={p.id} type="button" onClick={() => onApplyPreset(p)} disabled={applyingPreset === p.id} data-testid={`ai-booth-preset-${p.id}`}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2 disabled:opacity-60 ${activeVibe ? 'bg-white text-black shadow-[0_0_30px_-10px_rgba(255,255,255,0.6)]' : 'bg-white/[0.04] ring-1 ring-white/10 text-gray-300 hover:text-white hover:bg-white/[0.08]'}`}>
                    {applyingPreset === p.id && <Loader2 className="w-3 h-3 animate-spin" />}
                    {p.name}
                    <span className={`text-[10px] uppercase tracking-wide ${activeVibe ? 'text-black/60' : 'text-gray-500'}`}>{p.energy}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Main grid: NOW PLAYING hero + Shoutout inbox */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* HERO */}
            <section className="lg:col-span-7 rounded-3xl p-6 sm:p-8 bg-[#0E0E16]/80 border-2 border-[#00FFFF]/25 shadow-[0_0_35px_-10px_rgba(0,255,255,0.4),inset_0_0_20px_rgba(255,0,127,0.05)]">
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
                {/* Album cover + spinning vinyl */}
                <div className="relative flex-shrink-0">
                  <div className={`absolute -right-6 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-[radial-gradient(circle,#1a1a22_30%,#000_31%,#000_38%,#1a1a22_39%)] border border-white/10 ${playing ? 'animate-[spin_12s_linear_infinite]' : ''}`}
                    style={{ boxShadow: '0 0 30px -10px rgba(0,255,255,0.4)' }}>
                    <span className="absolute inset-0 m-auto w-8 h-8 rounded-full bg-[#FF007F]" />
                  </div>
                  <div className="relative w-44 h-44 sm:w-52 sm:h-52 rounded-2xl overflow-hidden border-2 border-[#FF007F] z-10"
                    style={{ animation: playing ? 'mb-breathe 3.5s ease-in-out infinite' : 'none', boxShadow: '0 0 40px -6px rgba(255,0,127,0.5)' }}>
                    <img src={COVER} alt="MusicBooth AI" className="w-full h-full object-cover" data-testid="ai-booth-cover" />
                    {/* equalizer overlay */}
                    {playing && (
                      <div className="absolute bottom-0 left-0 right-0 h-10 flex items-end gap-1 px-3 pb-2 bg-gradient-to-t from-black/70 to-transparent">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <span key={i} className="mb-eqbar flex-1 rounded-sm"
                            style={{ background: i % 2 ? '#00FFFF' : '#FF007F', animationDelay: `${i * 0.12}s`, height: '100%' }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Meta + controls */}
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <p className="text-[11px] uppercase tracking-[0.3em] font-bold flex items-center justify-center sm:justify-start gap-2" style={{ color: statusColor }}>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: statusColor }} /> {status}
                  </p>
                  <h1 className="mt-2 text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] break-words drop-shadow-[0_0_20px_rgba(255,0,127,0.4)]"
                    data-testid="ai-booth-title" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
                    <span className="font-extrabold">{(displayGenre || 'library').replace(/-/g, ' ')}</span>
                    <span className="text-gray-600 mx-2">·</span>
                    <span className="italic font-light text-[#00FFFF]">{displayEnergy}</span>
                  </h1>

                  {current?.title && (
                    <p className="mt-2 text-sm text-gray-300 truncate" data-testid="ai-booth-track">
                      <span className="text-white font-semibold">{current.title}</span>
                      <span className="text-gray-600"> — {current.artist || 'MusicBooth AI'}</span>
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap items-center justify-center sm:justify-start gap-2">
                    {!!current?.bpm && (
                      <span className="px-3 py-1.5 rounded-lg bg-[#FFD700]/10 ring-1 ring-[#FFD700]/30 text-xs text-[#FFD700] font-mono font-bold">{current.bpm} BPM</span>
                    )}
                    <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] ring-1 ring-white/10 text-xs text-gray-300 lowercase" data-testid="ai-booth-tod">{tod}</span>
                  </div>

                  {/* progress */}
                  {progress.duration > 0 && (
                    <div className="mt-4 flex items-center gap-3">
                      <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#FF007F,#00FFFF)' }} />
                      </div>
                      <span className="text-[11px] text-gray-500 font-mono shrink-0">
                        -{Math.floor(remainingS / 60)}:{String(Math.floor(remainingS % 60)).padStart(2, '0')}
                      </span>
                    </div>
                  )}
                  {crossfadeSoon && <p className="mt-1 text-[11px] text-[#00FFFF]">· beat-matched crossfade incoming</p>}

                  {/* transport */}
                  <div className="mt-5 flex items-center justify-center sm:justify-start gap-4">
                    <button type="button" onClick={playing ? pause : play} aria-label={playing ? 'Pause' : 'Play'}
                      data-testid={playing ? 'ai-booth-pause' : 'ai-booth-play'}
                      className="relative w-20 h-20 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                      style={{ background: 'conic-gradient(from 90deg, #FFB347, #FF6EC7, #A855F7, #4C6EF5, #22D3EE, #34D399, #FACC15, #FFB347)', padding: '3px' }}>
                      <span className="absolute inset-[3px] rounded-full bg-black" />
                      <span className="relative w-full h-full rounded-full bg-white/[0.06] grid place-items-center ring-1 ring-white/10">
                        {playing ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white ml-1" />}
                      </span>
                    </button>
                    <button type="button" onClick={skip} data-testid="ai-booth-skip"
                      className="px-5 py-3 rounded-xl bg-white/[0.04] ring-1 ring-white/10 hover:bg-white/[0.08] text-gray-300 hover:text-white text-xs font-bold uppercase tracking-[0.25em] flex items-center gap-2 transition-colors">
                      <SkipForward className="w-4 h-4" /> Skip
                    </button>
                  </div>

                  {nextTrack && (
                    <p className="mt-4 text-[11px] text-gray-500" data-testid="ai-booth-next">
                      <Radio className="w-3 h-3 inline mr-1 text-[#39FF14]" /> Up next: <span className="text-gray-300">{nextTrack.title}</span>
                      <span className="text-gray-600"> — {nextTrack.artist}</span>
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* SHOUTOUT INBOX */}
            <div className="lg:col-span-5">
              <AiShoutoutInbox />
            </div>
          </div>

          {/* MIXER */}
          <section className="rounded-3xl p-6 lg:p-8 bg-[#0E0E16]/80 border-2 border-[#FF007F]/25 shadow-[0_0_30px_-10px_rgba(255,0,127,0.35)]" data-testid="ai-booth-mixer">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF007F] font-bold">Mixer</p>
                <h2 className="mt-1 text-2xl lg:text-3xl font-light" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>Shape the mood</h2>
              </div>
              {settings?.mode === 'auto' && (
                <p className="text-[11px] text-gray-500">On <span className="text-white font-semibold">Auto</span> — applying a mood switches to Manual.</p>
              )}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold">Energy</p>
                  {draftEnergy && <p className="text-sm text-[#39FF14] capitalize font-bold">{draftEnergy}</p>}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {(taxonomy.energies || []).map((e) => {
                    const active = draftEnergy === e;
                    return (
                      <button key={e} type="button" onClick={() => setDraftEnergy(e)} data-testid={`ai-energy-${e}`}
                        className={`py-3 rounded-xl text-xs font-bold uppercase tracking-[0.15em] transition-all ${active ? 'bg-[#FF007F] text-white shadow-[0_0_40px_-8px_rgba(255,0,127,0.8)]' : 'bg-black/40 ring-1 ring-white/10 text-gray-400 hover:text-white hover:ring-[#00FFFF]/40'}`}>
                        {e}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-3">Genre</p>
                <div className="relative">
                  <select value={draftGenre || ''} onChange={(e) => setDraftGenre(e.target.value)} data-testid="ai-genre-select"
                    className="w-full appearance-none bg-black/50 ring-1 ring-white/10 rounded-xl px-4 py-3.5 text-sm text-white pr-10 focus:outline-none focus:ring-[#00FFFF]/40 hover:ring-white/20 transition">
                    <option value="" disabled>Pick a genre</option>
                    {(taxonomy.genres || []).map((g) => (
                      <option key={g} value={g}>{g.replace(/-/g, ' ')}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">▾</span>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button type="button" onClick={applyMoodClick} disabled={applying || !draftEnergy || !draftGenre} data-testid="ai-apply-mood"
                className="px-6 py-3 rounded-xl bg-[#FF007F] hover:bg-[#e6006f] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-[0.25em] transition-colors shadow-[0_0_40px_-8px_rgba(255,0,127,0.8)]">
                {applying ? 'Applying…' : 'Apply mood — beat-matched crossfade'}
              </button>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
